import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Modal from "../../components/Modal";
import Alert from "../../components/Alert";
import useAuth from "../../hooks/useAuth";
import { isPlatformOperator } from "../../utils/authUtils";
import {
  listZkDevices,
  createZkDevice,
  deleteZkDevice,
  syncZkDeviceTime,
  queryZkDeviceTime,
  rebootZkDevice,
  listZkDeviceCommands,
} from "../../api/zkDeviceApi";
import "../Users/UsersPage.css";

const emptyForm = { serialNumber: "", name: "", instituteId: "", subjectType: "STUDENT", ipAddress: "", port: "", location: "" };

/**
 * Real ZKTeco ADMS/iClock device management — ported from the sibling
 * zkteco-final-v2 project (see ZkDevice entity javadoc on the backend).
 * A device pings in on its own (auto-registers as "Unassigned Device ...")
 * the first time it's pointed at this server; this page is where it then
 * gets assigned to an institute and a student/staff population.
 */
export default function ZkDevicesPage() {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [commandsFor, setCommandsFor] = useState(null);
  const [commands, setCommands] = useState([]);

  const load = () => {
    setLoading(true);
    listZkDevices()
      .then(setDevices)
      .catch(() => setError("Failed to load devices."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await createZkDevice({
        ...form,
        instituteId: form.instituteId ? Number(form.instituteId) : undefined,
        port: form.port ? Number(form.port) : undefined,
      });
      setSuccess("Device registered.");
      setModalOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to register device.");
    }
  };

  const handleDelete = async (device) => {
    if (!window.confirm(`Delete device "${device.name}" (${device.serialNumber})?`)) return;
    setError("");
    try {
      await deleteZkDevice(device.id);
      setSuccess("Device deleted.");
      setDevices((prev) => prev.filter((d) => d.id !== device.id));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete device.");
    }
  };

  const handleAction = async (device, action, label) => {
    setError("");
    try {
      await action(device.id);
      setSuccess(`${label} queued for "${device.name}" — applies on the device's next check-in.`);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || `Failed to queue ${label}.`);
    }
  };

  const openCommands = async (device) => {
    setCommandsFor(device);
    setCommands(await listZkDeviceCommands(device.id));
  };

  const columns = [
    { key: "serialNumber", label: "Serial" },
    { key: "name", label: "Name" },
    { key: "instituteName", label: "Institute", render: (row) => row.instituteName || "— unassigned —" },
    { key: "subjectType", label: "Population", render: (row) => row.subjectType || "—" },
    { key: "status", label: "Status", render: (row) => (row.status === "ONLINE" ? "Online" : "Offline") },
    { key: "lastHeartbeat", label: "Last Seen", render: (row) => row.lastHeartbeat || "never" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>ZKTeco Devices</h1>
        <button type="button" className="btn-primary" onClick={() => setModalOpen(true)}>
          + Register Device
        </button>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <p>
        A device pings this server on its own the first time it's pointed here and shows up below as "Unassigned" —
        assign it to an institute and a population (Student/Staff) so its punches feed that population's attendance.
      </p>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={devices}
          actions={(device) => (
            <>
              <button type="button" className="btn-link" onClick={() => handleAction(device, syncZkDeviceTime, "Time sync")}>
                Sync Time
              </button>
              <button type="button" className="btn-link" onClick={() => handleAction(device, queryZkDeviceTime, "Info query")}>
                Query Info
              </button>
              <button type="button" className="btn-link" onClick={() => handleAction(device, rebootZkDevice, "Reboot")}>
                Reboot
              </button>
              <button type="button" className="btn-link" onClick={() => openCommands(device)}>
                Commands
              </button>
              <button type="button" className="btn-link danger" onClick={() => handleDelete(device)}>
                Delete
              </button>
            </>
          )}
        />
      )}

      {modalOpen && (
        <Modal title="Register Device" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleCreate} className="user-form">
            <label>Serial Number *</label>
            <input value={form.serialNumber} onChange={(e) => setForm({ ...form, serialNumber: e.target.value })} required />
            <label>Name *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            {isPlatformOperator(user) && (
              <>
                <label>Institute ID</label>
                <input value={form.instituteId} onChange={(e) => setForm({ ...form, instituteId: e.target.value })} />
              </>
            )}
            <label>Population</label>
            <select value={form.subjectType} onChange={(e) => setForm({ ...form, subjectType: e.target.value })}>
              <option value="STUDENT">Student</option>
              <option value="STAFF">Staff</option>
            </select>
            <label>IP Address</label>
            <input value={form.ipAddress} onChange={(e) => setForm({ ...form, ipAddress: e.target.value })} />
            <label>Port</label>
            <input value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} />
            <label>Location</label>
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Main Gate" />
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                Register
              </button>
            </div>
          </form>
        </Modal>
      )}

      {commandsFor && (
        <Modal title={`Command History — ${commandsFor.name}`} onClose={() => setCommandsFor(null)}>
          <DataTable
            columns={[
              { key: "commandType", label: "Command" },
              { key: "status", label: "Status" },
              { key: "responseText", label: "Response" },
              { key: "createdAt", label: "Queued" },
            ]}
            rows={commands}
          />
        </Modal>
      )}
    </MainLayout>
  );
}
